import { useEffect, useRef } from "react";
import type { PageType } from "../types";

// Pages éphémères — pas de push URL (session active, agent sélectionné)
const TRANSIENT_PAGES: PageType[] = ["training", "coach", "agentPresentation"];

interface ParsedRoute {
  page: PageType;
  challengeId?: string;
  adminAccountId?: string;
  /** Espace encadrant : piloter | animer | concevoir. */
  encadrantBloc?: string;
  /** Sous-écran de cet espace (cockpit, campaigns, offers…). */
  encadrantSub?: string;
  /** Fiche collaborateur ouverte (`/encadrant/piloter/activite/:userId`). */
  memberId?: string;
}

/** URL → page + params */
export function parseUrl(pathname: string): ParsedRoute {
  const path = pathname.replace(/\/+$/, "") || "/"; // trim trailing slashes

  if (path === "/dashboard" || path === "/") return { page: "dashboard" };
  if (path === "/history") return { page: "history" };
  if (path === "/help") return { page: "help" };
  if (path === "/reporting") return { page: "reporting" };
  if (path === "/account") return { page: "account" };
  if (path === "/admin") return { page: "admin" };
  if (path === "/admin/coaching-briefs") return { page: "admin-coaching-briefs" };
  if (path === "/admin/evaluation-criteria") return { page: "admin-eval-criteria-matrix" };
  if (path === "/admin/kestio-knowledge") return { page: "admin-kestio-knowledge" };
  if (path === "/admin/llm") return { page: "admin-llm-routes" };
  if (path === "/auth") return { page: "auth" };
  if (path === "/challenges") return { page: "challenge" };
  // Ajoutées le 25/07 : ces pages étaient ciblées par des notifications alors
  // qu'elles n'avaient pas d'URL — le clic retombait silencieusement sur le
  // tableau de bord. Toute page visée par un CTA doit être adressable.
  if (path === "/m-entrainer") return { page: "m-entrainer" };
  if (path === "/rewards") return { page: "rewards" };
  if (path === "/progress") return { page: "my-progress" };
  if (path === "/sessions/live") return { page: "sessions-live" };
  if (path === "/sessions/training") return { page: "sessions-training" };
  if (path === "/paths") return { page: "certification-paths" };
  if (path === "/notifications") return { page: "notifications" };
  if (path === "/notifications/preferences") return { page: "notification-preferences" };
  // Les sous-écrans encadrant sont adressables : sans ça, un rechargement de
  // page ramenait toujours au cockpit — le manager perdait son onglet, et
  // aucun lien vers « Agir › Campagnes » n'était partageable.
  // La fiche collaborateur a une adresse : /encadrant/piloter/activite/:userId
  // (spec/pilot-cible.md, chantier C) — relue ici, puis portée par `?member=`.
  const encadrantMatch = path.match(/^\/encadrant(?:\/([a-z]+))?(?:\/([a-z-]+))?(?:\/([a-f0-9-]{36}))?$/i);
  if (encadrantMatch) {
    return {
      page: "encadrant",
      encadrantBloc: encadrantMatch[1],
      encadrantSub: encadrantMatch[2],
      memberId: encadrantMatch[3],
    };
  }

  // /challenges/:id
  const challengeMatch = path.match(/^\/challenges\/([a-f0-9-]+)$/i);
  if (challengeMatch) return { page: "challenge", challengeId: challengeMatch[1] };

  // /admin/account/:id
  const adminMatch = path.match(/^\/admin\/account\/([a-f0-9-]+)$/i);
  if (adminMatch) return { page: "admin-account", adminAccountId: adminMatch[1] };

  // Fallback
  return { page: "dashboard" };
}

/** State → URL (retourne null pour les pages éphémères) */
export function pageToUrl(
  page: PageType,
  challengeId: string | null,
  adminAccountId: string | null,
  /** Espace et sous-écran encadrant, pour rendre chacun adressable. */
  encadrantBloc?: string,
  encadrantSub?: string,
): string | null {
  if (TRANSIENT_PAGES.includes(page)) return null;

  switch (page) {
    case "dashboard": return "/dashboard";
    case "history": return "/history";
    case "help": return "/help";
    case "reporting": return "/reporting";
    case "account": return "/account";
    case "admin": return "/admin";
    case "admin-coaching-briefs": return "/admin/coaching-briefs";
    case "admin-eval-criteria-matrix": return "/admin/evaluation-criteria";
    case "admin-kestio-knowledge": return "/admin/kestio-knowledge";
    case "admin-llm-routes": return "/admin/llm";
    case "auth": return "/auth";
    case "m-entrainer": return "/m-entrainer";
    case "rewards": return "/rewards";
    case "my-progress": return "/progress";
    case "sessions-live": return "/sessions/live";
    case "sessions-training": return "/sessions/training";
    case "certification-paths": return "/paths";
    case "notifications": return "/notifications";
    case "notification-preferences": return "/notifications/preferences";
    case "encadrant":
      return encadrantBloc
        ? `/encadrant/${encadrantBloc}${encadrantSub ? `/${encadrantSub}` : ""}`
        : "/encadrant";
    case "challenge":
      return challengeId ? `/challenges/${challengeId}` : "/challenges";
    case "admin-account":
      return adminAccountId ? `/admin/account/${adminAccountId}` : "/admin";
    default: return "/dashboard";
  }
}

interface UseUrlSyncParams {
  currentPage: PageType;
  selectedChallengeId: string | null;
  adminTargetAccountId: string | null;
  isAuthenticated: boolean;
  setCurrentPage: (p: PageType) => void;
  setSelectedChallengeId: (id: string | null) => void;
  setAdminTargetAccountId: (id: string | null) => void;
  setChallengeBackPage: (p: PageType) => void;
  /** Espace encadrant courant — poussé dans l'URL et relu au chargement. */
  encadrantBloc?: string;
  encadrantSub?: string;
  setEncadrantBloc?: (b: string) => void;
  setEncadrantSub?: (s: string | undefined) => void;
  enabled: boolean;
}

export function useUrlSync(params: UseUrlSyncParams) {
  const {
    currentPage, selectedChallengeId, adminTargetAccountId,
    isAuthenticated,
    setCurrentPage, setSelectedChallengeId, setAdminTargetAccountId,
    setChallengeBackPage,
    encadrantBloc, encadrantSub, setEncadrantBloc, setEncadrantSub,
    enabled,
  } = params;

  const fromPopstate = useRef(false);
  const initialized = useRef(false);

  // Effect 1: Read URL on mount (initial page load)
  useEffect(() => {
    if (!enabled || !isAuthenticated || initialized.current) return;
    initialized.current = true;

    const pathname = window.location.pathname;
    // Skip special paths handled elsewhere
    if (pathname.startsWith("/signup") || pathname === "/auth/verify") return;
    // Skip if legacy deep link params are present (handled by existing useEffect)
    if (window.location.search.includes("page=challenge")) return;

    const parsed = parseUrl(pathname);

    // Only update state if URL points to a non-default page
    if (parsed.page !== "dashboard" || pathname !== "/") {
      fromPopstate.current = true; // prevent push-back
      setCurrentPage(parsed.page);
      if (parsed.challengeId) setSelectedChallengeId(parsed.challengeId);
      if (parsed.adminAccountId) setAdminTargetAccountId(parsed.adminAccountId);
      if (parsed.encadrantBloc) setEncadrantBloc?.(parsed.encadrantBloc);
      if (parsed.encadrantSub) setEncadrantSub?.(parsed.encadrantSub);
      // /encadrant/piloter/activite/:userId → même écran, fiche ouverte via ?member=
      if (parsed.memberId && parsed.encadrantBloc && parsed.encadrantSub) {
        window.history.replaceState(null, "", `/encadrant/${parsed.encadrantBloc}/${parsed.encadrantSub}?member=${parsed.memberId}`);
      }
    }

    // Normalize "/" to "/dashboard"
    if (pathname === "/") {
      window.history.replaceState(null, "", "/dashboard");
    }
  }, [enabled, isAuthenticated]);

  // Effect 2: Push URL when state changes
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;

    // Skip if this change came from popstate (back button)
    if (fromPopstate.current) {
      fromPopstate.current = false;
      return;
    }

    const targetUrl = pageToUrl(currentPage, selectedChallengeId, adminTargetAccountId, encadrantBloc, encadrantSub);
    if (!targetUrl) return; // transient page, don't push

    // Only push if URL actually changed
    if (window.location.pathname !== targetUrl) {
      window.history.pushState(
        { page: currentPage, challengeId: selectedChallengeId, adminAccountId: adminTargetAccountId },
        "",
        targetUrl,
      );
    }
  }, [enabled, isAuthenticated, currentPage, selectedChallengeId, adminTargetAccountId, encadrantBloc, encadrantSub]);

  // Effect 3: Listen to popstate (back/forward button)
  useEffect(() => {
    if (!enabled) return;

    const handlePopstate = (e: PopStateEvent) => {
      if (!isAuthenticated) return;

      fromPopstate.current = true;

      // Try to restore from history state first (reliable)
      if (e.state?.page) {
        setCurrentPage(e.state.page);
        setSelectedChallengeId(e.state.challengeId || null);
        setAdminTargetAccountId(e.state.adminAccountId || null);
        if (e.state.page === "challenge" && e.state.challengeId) {
          setChallengeBackPage("dashboard");
        }
        return;
      }

      // Fallback: parse URL
      const parsed = parseUrl(window.location.pathname);
      setCurrentPage(parsed.page);
      setSelectedChallengeId(parsed.challengeId || null);
      setAdminTargetAccountId(parsed.adminAccountId || null);
    };

    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [enabled, isAuthenticated]);
}
